var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Stack } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../../enum';
import { Autocomplete, ConfirmDialog, ErrorMessageBox, FallbackComponent, FormErrorText, FormField, NumberInput, } from '../../common';
import { BasePanel } from '../common/basePanel';
import { BaseVolumeTable } from '../common/baseTable';
import { QueuePanel } from '../common/queuePanel';
import { StepContainer, StepPanel } from '../common/stepContainer';
import { TIME_INTERVAL, WizardStep } from '../constants';
import { WizardContext } from '../context';
import { useCancelAndDeleteMultiskill, usePlanStatus, useQueueAPI, useQueueStatus, useQueuesHasMultiskill, } from '../queue/selectors';
import { QueueStatusType } from '../queue/types';
import { useEffectiveCapacityPermission, useIsPlanLocked, usePlanAPI, usePlanType, useWizardParams, } from '../selectors';
import { useChangeIntervalAPI } from '../workArrivalPattern/selectors';
import { FteForm } from './fteForm';
import { PartTimeForm } from './partTimeForm';
export var WorkingDayContent = function () {
    var _a, _b, _c;
    var t = useTranslation('wizard', { keyPrefix: 'workingDay' }).t;
    var status = useQueueStatus();
    var isPlanWAP = usePlanStatus(QueueStatusType.WAT);
    var queueData = useQueueAPI()[0];
    var planData = usePlanAPI()[0];
    var capacityPermission = useEffectiveCapacityPermission();
    var fetchCancelAndDeleteMultiskill = useCancelAndDeleteMultiskill();
    var isPlanLocked = useIsPlanLocked();
    var disabled = capacityPermission !== StatusPermission.Edit || isPlanLocked;
    var isDisableDailyOperating = !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.isDeferrable) || !!(status === null || status === void 0 ? void 0 : status.wat) || disabled;
    var _d = useState(), pendingInterval = _d[0], setPendingInterval = _d[1];
    var _e = useChangeIntervalAPI(), changeIntervalData = _e[0], changeInterval = _e[1], clearIntervalData = _e[2];
    var step = useWizardParams().step;
    var isReview = step === WizardStep.Review;
    var _f = useContext(WizardContext), handleSave = _f.handleSave, _g = _f.methods, control = _g.control, setValue = _g.setValue, errors = _g.formState.errors;
    var hasMultiskill = useQueuesHasMultiskill();
    useEffect(function () {
        return function () { return clearIntervalData(); };
    }, [clearIntervalData]);
    var handleChangeInterval = function (interval) {
        setPendingInterval(interval);
    };
    var handleContinue = function () {
        fetchCancelAndDeleteMultiskill();
        setPendingInterval(undefined);
        return handleSave('submit', function () { var _a; return changeInterval({ basePlanId: (_a = planData.data) === null || _a === void 0 ? void 0 : _a.basePlanId, timeInterval: pendingInterval }); })();
    };
    var handleClose = function () {
        var _a;
        setValue('timeInterval', (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.timeInterval, { shouldDirty: true });
        setPendingInterval(undefined);
    };
    return (_jsxs(FallbackComponent, __assign({ requesting: !!changeIntervalData.requesting, overlay: true }, { children: [_jsx(ErrorMessageBox, { errors: [changeIntervalData.error] }), _jsx(BaseVolumeTable, { name: "workingDays", disabled: !!(status === null || status === void 0 ? void 0 : status.wat) || disabled, calculateWorkingDate: true }), _jsxs(Box, __assign({ sx: { mt: isReview ? 2 : 0 } }, { children: [_jsxs(Stack, __assign({ direction: "row", spacing: 4 }, { children: [_jsx(FormField, __assign({ required: true, name: "timeInterval", label: t('timeInterval'), control: control, unitLabel: t('min') }, { children: _jsx(Autocomplete, { options: TIME_INTERVAL, "data-testid": "timeInterval", getOptionLabel: function (_a) {
                                        var id = _a.id;
                                        return "".concat(id);
                                    }, disabled: !!isPlanWAP || !((_b = planData.data) === null || _b === void 0 ? void 0 : _b.isBaseline) || disabled, onChange: handleChangeInterval }) })), _jsx(FormField, __assign({ required: true, name: "dailyOperatingHours", label: t('dailyOperatingHours'), control: control, unitLabel: t('hrs') }, { children: _jsx(NumberInput, { min: 1, max: 24, decimal: 2, disabled: isDisableDailyOperating, placeholder: isDisableDailyOperating ? '-' : '' }) }))] })), _jsx(FormErrorText, { message: (_c = errors === null || errors === void 0 ? void 0 : errors.fteDay) === null || _c === void 0 ? void 0 : _c.message, scrollIntoView: false, bigText: false })] })), _jsx(FteForm, {}), _jsx(PartTimeForm, {}), _jsx(ConfirmDialog, { open: !!pendingInterval && !!hasMultiskill, onClose: handleClose, message: t('confirm'), confirmButtonText: "continue", onConfirm: handleContinue })] })));
};
export var WorkingDayStep = function () {
    var t = useTranslation('wizard', { keyPrefix: 'workingDay' }).t;
    var planType = usePlanType();
    return (_jsx(StepContainer, __assign({ leftContent: _jsx(QueuePanel, {}) }, { children: _jsx(StepPanel, { children: _jsx(BasePanel, __assign({ title: t('title', { planType: planType }), statusType: QueueStatusType.WorkingDays, needSaveBeforeCopy: true }, { children: _jsx(WorkingDayContent, {}) })) }) })));
};
